import { EpisodeCategory } from '@common/enums';

/**
 * List of episode categories that have medical protocols available.
 * This is used to determine whether to show medical protocol related UI elements.
 */
export const medicalProtocolAvailableEpisodeCategories = [
    EpisodeCategory.ophthalmology,
    EpisodeCategory.mentalHealth,
    EpisodeCategory.somnology,
    EpisodeCategory.dermatology,
    EpisodeCategory.cardiology,
] as const;

export type MedicalProtocolEpisodeCategory = (typeof medicalProtocolAvailableEpisodeCategories)[number];

export type MedicalProtocolConfig = {
    url: string;
};

const medicalProtocolBaseUrl = 'https://webshare.zenya.work/5nxr72q2158sn5jr/Document.aspx?websharedocumentid=';

const medicalProtocolDocumentIds: Record<MedicalProtocolEpisodeCategory, string> = {
    [EpisodeCategory.ophthalmology]: 'dcec81f3-1bf7-4a55-98cd-69751dc80a75',
    [EpisodeCategory.mentalHealth]: 'e3404051-0633-4686-9e01-cbda73faac17',
    [EpisodeCategory.somnology]: 'b35669da-7238-460f-9093-cdf6f56cf86f',
    [EpisodeCategory.dermatology]: 'bcc1be63-da3b-4460-8dd8-9630491d3d13',
    [EpisodeCategory.cardiology]: '75da2c3e-2b13-43c9-9422-730eeaf7f7ac',
};

const medicalProtocolConfigs: Record<MedicalProtocolEpisodeCategory, MedicalProtocolConfig> = {
    [EpisodeCategory.ophthalmology]: {
        url: `${medicalProtocolBaseUrl}${medicalProtocolDocumentIds[EpisodeCategory.ophthalmology]}`,
    },
    [EpisodeCategory.mentalHealth]: {
        url: `${medicalProtocolBaseUrl}${medicalProtocolDocumentIds[EpisodeCategory.mentalHealth]}`,
    },
    [EpisodeCategory.somnology]: {
        url: `${medicalProtocolBaseUrl}${medicalProtocolDocumentIds[EpisodeCategory.somnology]}`,
    },
    [EpisodeCategory.dermatology]: {
        url: `${medicalProtocolBaseUrl}${medicalProtocolDocumentIds[EpisodeCategory.dermatology]}`,
    },
    [EpisodeCategory.cardiology]: {
        url: `${medicalProtocolBaseUrl}${medicalProtocolDocumentIds[EpisodeCategory.cardiology]}`,
    },
};

/**
 * Checks if medical protocols are available for a given episode category.
 * @param episodeCategory - The episode category to check
 * @returns true if medical protocols are available for this category, false otherwise
 */
export function isMedicalProtocolAvailable(episodeCategory: EpisodeCategory | null | undefined): boolean {
    if (!episodeCategory) {
        return false;
    }

    return medicalProtocolAvailableEpisodeCategories.includes(episodeCategory as MedicalProtocolEpisodeCategory);
}

/**
 * Gets the medical protocol URL for a given episode category.
 * @param episodeCategory - The episode category to get the URL for
 * @returns The URL for the medical protocol, or null if not available
 */
export function getMedicalProtocolUrl(episodeCategory: EpisodeCategory | null | undefined): string | null {
    if (!isMedicalProtocolAvailable(episodeCategory) || !episodeCategory) {
        return null;
    }

    return medicalProtocolConfigs[episodeCategory as MedicalProtocolEpisodeCategory].url;
}
